'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

// Fetch saved carts
export const useSavedOrders = () => {
  return useQuery({
    queryKey: ['saved-orders'],
    queryFn: async () => {
      const response = await fetch('/api/cart/search-carts?status=queued')
      if (!response.ok) {
        throw new Error('Failed to fetch saved orders')
      }
      const data = await response.json()
      // SuccessResponse returns the data directly, not wrapped in {data: ...}
      return Array.isArray(data) ? data : []
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
}

// Delete cart mutation
export const useDeleteCart = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (cartId: string) => {
      const response = await fetch('/api/cart/cancel-cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cartId }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: response.statusText || 'Failed to delete cart',
        }))
        throw new Error(error?.message ?? 'Failed to delete cart')
      }

      return await response.json()
    },
    onMutate: async (cartId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['saved-orders'] })

      // Snapshot the previous value
      const previousOrders = queryClient.getQueryData(['saved-orders'])

      // Optimistically update to the new value
      queryClient.setQueryData(['saved-orders'], (old: any[]) => {
        return old?.filter((order) => {
          const id = order?.cart?.id ?? order?.id
          return id !== cartId
        })
      })

      // Return context with the snapshot
      return { previousOrders }
    },
    onError: (error, cartId, context) => {
      // Rollback on error
      queryClient.setQueryData(['saved-orders'], context?.previousOrders)
      toast.error(error.message ?? 'Failed to delete order')
    },
    onSuccess: () => {
      toast.success('Order deleted successfully')
    },
    onSettled: () => {
      // Refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['saved-orders'] })
    },
  })
}

